// Collection Search Optimization
const CollectionSearch = {
  init() {
    document.querySelectorAll('[data-component="collection-search"]').forEach(section => {
      this.initializeSearch(section);
    });
  },

  initializeSearch(section) {
    const searchInput = section.querySelector('[data-search-input]');
    const searchResults = section.querySelector('[data-search-results]');
    const popularDestinations = section.querySelector('[data-popular-destinations]');
    const destinationsList = section.querySelector('[data-destinations-list]');
    const clearButton = section.querySelector('[data-clear-search]');
    
    if (!searchInput || !searchResults) return;

    // Cache DOM elements and frequently used data
    const cache = {
      destinations: null,
      popularDestinations: null,
      lastQuery: '',
      searchTimeout: null,
      visibleItems: new Set(),
      pageSize: 20,
      currentPage: 0
    };

    // Intersection Observer for lazy loading
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const item = entry.target;
          if (!cache.visibleItems.has(item.dataset.destinationId)) {
            cache.visibleItems.add(item.dataset.destinationId);
            loadItemImage(item);
          }
        }
      });
    }, {
      root: searchResults,
      rootMargin: '50px',
      threshold: 0.1
    });

    // Load image for destination item
    const loadItemImage = (item) => {
      const img = item.querySelector('img[data-src]');
      if (img) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      }
    };

    // Efficient debounce implementation
    const debounce = (fn, delay) => {
      let timeoutId;
      return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn.apply(this, args), delay);
      };
    };

    // Optimized destination filtering with pagination
    const filterDestinations = (query, page = 0) => {
      if (!cache.destinations) return [];
      
      const normalizedQuery = query.toLowerCase().trim();
      const filtered = cache.destinations.filter(destination => {
        const matchTitle = destination.title.toLowerCase().includes(normalizedQuery);
        const matchRegion = destination.region?.toLowerCase().includes(normalizedQuery);
        const matchCountry = destination.country?.toLowerCase().includes(normalizedQuery);
        return matchTitle || matchRegion || matchCountry;
      });

      const start = page * cache.pageSize;
      return filtered.slice(start, start + cache.pageSize);
    };

    // Efficient DOM updates using DocumentFragment and virtualization
    const renderDestinations = (destinations, container, append = false) => {
      const fragment = document.createDocumentFragment();
      
      destinations.forEach(destination => {
        const item = document.createElement('a');
        item.href = destination.url;
        item.className = 'destination-item';
        item.setAttribute('data-destination-id', destination.id);
        
        item.innerHTML = `
          <div class="destination-flag">
            <img data-src="${destination.flag}" alt="${destination.country}" loading="lazy" width="24" height="24">
          </div>
          <div class="destination-info">
            <div class="destination-title">${destination.title}</div>
            ${destination.region ? `<div class="destination-region">${destination.region}</div>` : ''}
          </div>
          ${destination.price ? `<div class="destination-price">$${destination.price}</div>` : ''}
        `;
        
        fragment.appendChild(item);
        observer.observe(item);
      });

      if (!append) {
        container.innerHTML = '';
      }
      container.appendChild(fragment);
    };

    // Infinite scroll handler
    const handleScroll = debounce(() => {
      const scrollPosition = searchResults.scrollTop + searchResults.clientHeight;
      const scrollHeight = searchResults.scrollHeight;
      
      if (scrollHeight - scrollPosition < 100) {
        cache.currentPage++;
        const moreDestinations = filterDestinations(cache.lastQuery, cache.currentPage);
        if (moreDestinations.length > 0) {
          renderDestinations(moreDestinations, destinationsList, true);
        }
      }
    }, 100);

    // Optimized search handler with caching and pagination
    const handleSearch = debounce(async (query) => {
      if (query === cache.lastQuery) return;
      cache.lastQuery = query;
      cache.currentPage = 0;

      if (query.length < 2) {
        searchResults.classList.remove('sc-open');
        return;
      }

      try {
        searchResults.classList.add('sc-open');
        
        // Show loading state only for initial load
        if (!cache.destinations) {
          searchResults.innerHTML = '<div class="search-loading">Searching...</div>';
          
          const response = await fetch('/search/suggest.json?q=&resources[type]=product&resources[limit]=250&resources[options][fields]=title,handle,variants,tags');
          const data = await response.json();
          cache.destinations = data.resources.results.products.map(product => ({
            id: product.id,
            title: product.title,
            url: `/products/${product.handle}`,
            price: product.variants[0]?.price ? (product.variants[0].price/100).toFixed(2) : null,
            country: product.tags.find(tag => tag.startsWith('country:'))?.split(':')[1],
            region: product.tags.find(tag => tag.startsWith('region:'))?.split(':')[1],
            flag: product.featured_image?.url || ''
          }));
        }

        const filteredDestinations = filterDestinations(query);
        
        if (filteredDestinations.length > 0) {
          renderDestinations(filteredDestinations, destinationsList);
          destinationsList.closest('.countries-section').style.display = 'block';
        } else {
          destinationsList.innerHTML = '<div class="no-results">No destinations found</div>';
        }

        if (popularDestinations) {
          popularDestinations.closest('.popular-destinations').style.display = 
            query.length > 0 ? 'none' : 'block';
        }

      } catch (error) {
        console.error('Search error:', error);
        searchResults.innerHTML = '<div class="search-error">An error occurred while searching</div>';
      }
    }, 300);

    // Event Listeners
    searchInput.addEventListener('input', (e) => handleSearch(e.target.value));
    searchResults.addEventListener('scroll', handleScroll);
    
    if (clearButton) {
      clearButton.addEventListener('click', () => {
        searchInput.value = '';
        handleSearch('');
        searchInput.focus();
      });
    }

    // Click outside handler with optimization
    const handleClickOutside = (event) => {
      if (!searchResults.contains(event.target) && !searchInput.contains(event.target)) {
        searchResults.classList.remove('sc-open');
      }
    };

    document.addEventListener('click', handleClickOutside);

    // Cleanup function
    return () => {
      observer.disconnect();
      document.removeEventListener('click', handleClickOutside);
      searchResults.removeEventListener('scroll', handleScroll);
    };
  },

  // Initialize popular destinations with virtualization
  async initializePopularDestinations(container) {
    if (!container) return;

    try {
      const response = await fetch('/collections/popular-destinations/products.json');
      const data = await response.json();
      
      const popularDestinations = data.products.map(product => ({
        id: product.id,
        title: product.title,
        url: `/products/${product.handle}`,
        price: product.variants[0]?.price ? (product.variants[0].price/100).toFixed(2) : null,
        country: product.tags.find(tag => tag.startsWith('country:'))?.split(':')[1],
        region: product.tags.find(tag => tag.startsWith('region:'))?.split(':')[1],
        flag: product.featured_image?.url || ''
      }));

      renderDestinations(popularDestinations.slice(0, 10), container);
    } catch (error) {
      console.error('Error loading popular destinations:', error);
      container.innerHTML = '<div class="error-message">Failed to load popular destinations</div>';
    }
  }
};

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  CollectionSearch.init();
}); 